import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { CourseSidebar } from '../../course/components/CourseSidebar';
import { Button, Icon } from 'semantic-ui-react';
import MarkdownTextarea from 'src/components/common/markdown-textarea';
import ReactPlayer from 'react-player';
import { screenSizes } from 'src/assets/styles/Literals';

const EditorStyles = styled.div`
  display: flex;
  min-height: calc(100vh - 4rem);
  max-width: 1400px;
  margin: 0 auto;
  background-color: #ffffff;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);

  @media (max-width: ${screenSizes.tablet}) {
    flex-direction: column;
    box-shadow: none;
  }

  .editor {
    .content-area {
      flex: 1;
      padding: 2rem;
      background-color: #ffffff;
      position: relative;
      overflow-y: auto;

      @media (max-width: ${screenSizes.tablet}) {
        padding: 1rem;
        min-height: calc(100vh - 4rem);
      }

      @media (max-width: ${screenSizes.mobile}) {
        padding: 0.5rem;
      }

      .editor-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 2rem;
        padding-bottom: 1rem;
        border-bottom: 2px solid #e0e0e0;

        @media (max-width: ${screenSizes.tablet}) {
          flex-direction: column;
          gap: 1rem;
          align-items: stretch;
        }

        .editor-title {
          font-family: 'Raleway', sans-serif;
          color: #2c3e50;
          font-size: 1.5rem;
          margin: 0;

          @media (max-width: ${screenSizes.tablet}) {
            font-size: 1.25rem;
          }
        }

        .editor-controls {
          display: flex;
          gap: 1rem;
          align-items: center;

          @media (max-width: ${screenSizes.tablet}) {
            flex-direction: column;
            gap: 0.5rem;
          }

          .view-toggle {
            display: flex;
            background-color: #f8f9fa;
            border-radius: 8px;
            padding: 0.25rem;
            border: 1px solid #e0e0e0;

            .toggle-button {
              padding: 0.5rem 1rem;
              border: none;
              border-radius: 6px;
              background-color: transparent;
              color: #666;
              cursor: pointer;
              font-size: 0.9rem;
              transition: all 0.2s ease;

              &:hover {
                background-color: #e9ecef;
              }

              &.active {
                background-color: #4caf50;
                color: white;

                &:hover {
                  background-color: #45a049;
                }
              }
            }
          }
        }
      }

      .content-container {
        width: 100%;
        max-width: ${screenSizes.desktop};
        margin: 0 auto;
        padding: 0 1rem;

        @media (max-width: ${screenSizes.tablet}) {
          padding: 0 0.5rem;
        }

        @media (max-width: ${screenSizes.mobile}) {
          padding: 0 0.25rem;
        }

        .title {
          font-family: 'Raleway', sans-serif;
          margin-bottom: 2rem;
          color: #2c3e50;
          font-size: 2rem;
          line-height: 1.3;

          @media (max-width: ${screenSizes.tablet}) {
            font-size: 1.75rem;
            margin-bottom: 1.5rem;
          }

          @media (max-width: ${screenSizes.mobile}) {
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
        }

        .editable-title {
          font-family: 'Raleway', sans-serif;
          margin-bottom: 2rem;
          color: #2c3e50;
          font-size: 2rem;
          line-height: 1.3;
          border: none;
          border-bottom: 2px solid #e0e0e0;
          background: transparent;
          width: 100%;
          padding: 0.5rem 0;
          transition: border-color 0.2s ease;

          &:focus {
            outline: none;
            border-bottom-color: #4caf50;
          }

          @media (max-width: ${screenSizes.tablet}) {
            font-size: 1.75rem;
            margin-bottom: 1.5rem;
          }

          @media (max-width: ${screenSizes.mobile}) {
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
        }

        .video-container {
          position: relative;
          padding-top: 56.25%; /* 16:9 Aspect Ratio */
          margin-bottom: 2rem;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          background-color: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;

          @media (max-width: ${screenSizes.tablet}) {
            margin-bottom: 1.5rem;
          }

          @media (max-width: ${screenSizes.mobile}) {
            margin-bottom: 1rem;
          }

          .video-placeholder {
            text-align: center;
            color: #666;
            font-size: 1.1rem;
          }

          .react-player {
            position: absolute;
            top: 0;
            left: 0;
          }
        }

        .video-input {
          width: 100%;
          padding: 1rem;
          border: 2px dashed #ccc;
          border-radius: 8px;
          background-color: #f8f9fa;
          font-size: 1rem;
          margin-bottom: 1rem;

          &:focus {
            outline: none;
            border-color: #4caf50;
          }
        }

        .text-content {
          line-height: 1.6;
          margin-bottom: 2rem;
          font-size: 1rem;

          p {
            margin-bottom: 1rem;
          }

          img {
            max-width: 100%;
            height: auto;
            margin: 1rem 0;
            border-radius: 4px;
          }

          @media (max-width: ${screenSizes.tablet}) {
            font-size: 0.95rem;
            margin-bottom: 1.5rem;
          }

          @media (max-width: ${screenSizes.mobile}) {
            font-size: 0.9rem;
            margin-bottom: 1rem;
          }
        }

        .markdown-editor {
          margin-bottom: 2rem;
        }

        .preview-content {
          line-height: 1.6;
          margin-bottom: 2rem;
          font-size: 1rem;
          padding: 1rem;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          background-color: #f8f9fa;

          p {
            margin-bottom: 1rem;
          }

          img {
            max-width: 100%;
            height: auto;
            margin: 1rem 0;
            border-radius: 4px;
          }

          @media (max-width: ${screenSizes.tablet}) {
            font-size: 0.95rem;
            margin-bottom: 1.5rem;
          }

          @media (max-width: ${screenSizes.mobile}) {
            font-size: 0.9rem;
            margin-bottom: 1rem;
          }
        }

        .quiz-container {
          background-color: #f8f9fa;
          padding: 2rem;
          border-radius: 8px;
          margin-bottom: 2rem;

          @media (max-width: ${screenSizes.tablet}) {
            padding: 1.5rem;
            margin-bottom: 1.5rem;
          }

          @media (max-width: ${screenSizes.mobile}) {
            padding: 1rem;
            margin-bottom: 1rem;
          }

          .quiz-header {
            margin-bottom: 2rem;
            text-align: center;

            h3 {
              font-family: 'Raleway', sans-serif;
              color: #2c3e50;
              margin-bottom: 1rem;

              @media (max-width: ${screenSizes.tablet}) {
                font-size: 1.5rem;
              }

              @media (max-width: ${screenSizes.mobile}) {
                font-size: 1.25rem;
              }
            }

            @media (max-width: ${screenSizes.tablet}) {
              margin-bottom: 1.5rem;
            }

            @media (max-width: ${screenSizes.mobile}) {
              margin-bottom: 1rem;
            }
          }

          .quiz-question {
            background-color: white;
            padding: 1.5rem;
            border-radius: 8px;
            margin-bottom: 1rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

            .question-input {
              width: 100%;
              padding: 0.75rem;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              font-size: 1rem;
              margin-bottom: 1rem;

              &:focus {
                outline: none;
                border-color: #4caf50;
              }
            }

            .option-input {
              width: 100%;
              padding: 0.5rem;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
              font-size: 0.9rem;
              margin-bottom: 0.5rem;

              &:focus {
                outline: none;
                border-color: #4caf50;
              }
            }

            .remove-button {
              background-color: #f44336;
              color: white;
              border: none;
              border-radius: 4px;
              padding: 0.25rem 0.5rem;
              cursor: pointer;
              font-size: 0.8rem;
              margin-left: 0.5rem;

              &:hover {
                background-color: #d32f2f;
              }
            }
          }

          .add-button {
            background-color: #4caf50;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 0.5rem 1rem;
            cursor: pointer;
            font-size: 0.9rem;
            margin-top: 0.5rem;

            &:hover {
              background-color: #45a049;
            }
          }
        }

        .navigation {
          display: flex;
          justify-content: center;
          margin-top: 2rem;
          padding: 2rem 0;
          border-top: 1px solid #e0e0e0;

          @media (max-width: ${screenSizes.tablet}) {
            margin-top: 1.5rem;
            padding: 1.5rem 0;
          }

          @media (max-width: ${screenSizes.mobile}) {
            margin-top: 1rem;
            padding: 1rem 0;
          }

          .styled-button {
            padding: 0.8rem 1.5rem;
            border: none;
            border-radius: 6px;
            color: white;
            cursor: pointer;
            font-family: 'Raleway', sans-serif;
            transition: all 0.2s ease;
            background-color: #4caf50;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 1rem;
            font-weight: 500;
            min-width: 200px;
            justify-content: center;

            &:hover {
              background-color: #45a049;
              transform: translateY(-2px);
              box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
            }

            &:disabled {
              cursor: not-allowed;
              background-color: #cccccc;
              transform: none;
              box-shadow: none;
            }

            @media (max-width: ${screenSizes.tablet}) {
              padding: 0.7rem 1.2rem;
              font-size: 0.95rem;
              min-width: 180px;
            }

            @media (max-width: ${screenSizes.mobile}) {
              padding: 0.6rem 1rem;
              font-size: 0.9rem;
              min-width: 160px;
              gap: 0.4rem;
            }
          }
        }
      }
    }
  }
`;

const LessonEditor = ({ lesson, course, onSave, onCancel }) => {
  const [viewMode, setViewMode] = useState('edit'); // 'edit' or 'preview'
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inputs, setInputs] = useState({
    title: '',
    description: '',
    content: '',
    category: '',
    order: '',
    courseId: '',
    type: 'text',
    videoUrl: '',
    duration: '',
    status: true,
    isLocked: false,
    requiresCompletion: false,
    attachments: [],
    quiz: {
      enabled: false,
      questions: [],
      passingScore: 70,
    },
  });
  const [errorInputs, setErrorInputs] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lesson) {
      setInputs({
        title: lesson.title || '',
        description: lesson.description || '',
        content: lesson.content || '',
        category: lesson.category || '',
        order: lesson.order || '',
        courseId: lesson.courseId || course?.id || '',
        type: lesson.type || 'text',
        videoUrl: lesson.videoUrl || lesson.link || '',
        duration: lesson.duration || '',
        status: lesson.status !== false,
        isLocked: lesson.isLocked || lesson.locked || false,
        requiresCompletion: lesson.requiresCompletion || false,
        attachments: lesson.attachments || lesson.downloads || [],
        quiz: lesson.quiz || {
          enabled: false,
          questions: [],
          passingScore: 70,
        },
      });
    }
  }, [lesson, course]);

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setInputs({ ...inputs, [name]: value });
    if (errorInputs && errorInputs[name]) {
      setErrorInputs({ ...errorInputs, [name]: false });
    }
  };

  const handleQuizChange = (field, value) => {
    setInputs({
      ...inputs,
      quiz: {
        ...inputs.quiz,
        [field]: value,
      },
    });
  };

  const addQuizQuestion = () => {
    const newQuestion = {
      question: '',
      options: ['', '', '', ''],
      correctAnswer: '',
    };
    handleQuizChange('questions', [...inputs.quiz.questions, newQuestion]);
  };

  const updateQuizQuestion = (index, field, value) => {
    const updatedQuestions = [...inputs.quiz.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value,
    };
    handleQuizChange('questions', updatedQuestions);
  };

  const removeQuizQuestion = (index) => {
    const updatedQuestions = inputs.quiz.questions.filter((_, i) => i !== index);
    handleQuizChange('questions', updatedQuestions);
  };

  const updateQuizOption = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...inputs.quiz.questions];
    updatedQuestions[questionIndex].options[optionIndex] = value;
    handleQuizChange('questions', updatedQuestions);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(inputs);
    } catch (error) {
      console.error('Error saving lesson:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedLessons = (course?.AcademyLessons || [])
    .filter((lesson) => lesson && typeof lesson === 'object')
    .map((lesson) => ({
      ...lesson,
      order: lesson.order || 0,
      category: lesson.category || 'Sin Categoría',
    }))
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .reduce((acc, lesson) => {
      const category = lesson.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(lesson);
      return acc;
    }, {});

  const handleLessonSelect = (selectedLesson) => {
    // In editor mode, we don't actually navigate, just show a message
    console.log('Lesson selected:', selectedLesson.title);
  };

  const renderContent = () => {
    if (viewMode === 'preview') {
      // Preview mode - show how it will look to students
      switch (inputs.type || 'text') {
        case 'video':
          return (
            <div className="video-container">
              {inputs.videoUrl ? (
                <ReactPlayer
                  url={inputs.videoUrl}
                  width="100%"
                  height="100%"
                  controls
                  playing={false}
                  className="react-player"
                  config={{
                    youtube: {
                      playerVars: { showinfo: 1 },
                    },
                    vimeo: {
                      playerOptions: { responsive: true },
                    },
                  }}
                />
              ) : (
                <div className="video-placeholder">Video URL no configurada</div>
              )}
            </div>
          );
        case 'text':
          return (
            <div className="text-content" dangerouslySetInnerHTML={{ __html: inputs.content }} />
          );
        case 'quiz':
          return (
            <div className="quiz-container">
              <div className="quiz-header">
                <h3>Evaluación</h3>
              </div>
              {inputs.quiz.questions.map((question, index) => (
                <div className="quiz-question" key={index}>
                  <h4>Pregunta {index + 1}</h4>
                  <p>{question.question}</p>
                  <ul>
                    {question.options.map((option, optionIndex) => (
                      <li key={optionIndex}>{option}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          );
        default:
          return (
            <div className="text-content" dangerouslySetInnerHTML={{ __html: inputs.content }} />
          );
      }
    } else {
      // Edit mode
      return (
        <>
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Tipo de Lección
            </label>
            <select
              name="type"
              value={inputs.type}
              onChange={handleInputs}
              style={{
                padding: '0.75rem',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                fontSize: '1rem',
                width: '100%',
                maxWidth: '300px',
              }}
            >
              <option value="text">Texto</option>
              <option value="video">Video</option>
              <option value="quiz">Quiz</option>
              <option value="pdf">PDF</option>
            </select>
          </div>

          {inputs.type === 'text' && (
            <div className="markdown-editor">
              <MarkdownTextarea
                inputs={inputs}
                name="content"
                label="Contenido de la lección"
                handleInputs={handleInputs}
                errorInputs={errorInputs}
                loading={loading}
              />
            </div>
          )}

          {inputs.type === 'video' && (
            <input
              name="videoUrl"
              value={inputs.videoUrl}
              onChange={handleInputs}
              placeholder="URL del video (YouTube, Vimeo, etc.)"
              className="video-input"
            />
          )}

          <div style={{ marginBottom: '2rem' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
              }}
            >
              <input
                type="checkbox"
                checked={inputs.quiz.enabled}
                onChange={() => handleQuizChange('enabled', !inputs.quiz.enabled)}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 'bold' }}>Incluir Quiz</span>
            </label>
          </div>

          {inputs.quiz.enabled && (
            <div className="quiz-container">
              <div className="quiz-header">
                <h3>Configurar Quiz</h3>
              </div>
              {inputs.quiz.questions.map((question, index) => (
                <div className="quiz-question" key={index}>
                  <input
                    value={question.question}
                    onChange={(e) => updateQuizQuestion(index, 'question', e.target.value)}
                    placeholder="Pregunta"
                    className="question-input"
                  />
                  {question.options.map((option, optionIndex) => (
                    <input
                      key={optionIndex}
                      value={option}
                      onChange={(e) => updateQuizOption(index, optionIndex, e.target.value)}
                      placeholder={`Opción ${optionIndex + 1}`}
                      className="option-input"
                    />
                  ))}
                  <input
                    value={question.correctAnswer}
                    onChange={(e) => updateQuizQuestion(index, 'correctAnswer', e.target.value)}
                    placeholder="Respuesta correcta"
                    className="question-input"
                  />
                  <button onClick={() => removeQuizQuestion(index)} className="remove-button">
                    Eliminar Pregunta
                  </button>
                </div>
              ))}
              <button onClick={addQuizQuestion} className="add-button">
                Agregar Pregunta
              </button>
            </div>
          )}
        </>
      );
    }
  };

  return (
    <EditorStyles>
      <CourseSidebar
        course={course}
        currentLesson={lesson}
        onLessonSelect={handleLessonSelect}
        progress={0}
        groupedLessons={groupedLessons}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <div className="editor">
        <div className="content-area">
          <div className="editor-header">
            <h2 className="editor-title">Editor de Lección</h2>
            <div className="editor-controls">
              <div className="view-toggle">
                <button
                  className={`toggle-button ${viewMode === 'edit' ? 'active' : ''}`}
                  onClick={() => setViewMode('edit')}
                >
                  <Icon name="edit" />
                  Editar
                </button>
                <button
                  className={`toggle-button ${viewMode === 'preview' ? 'active' : ''}`}
                  onClick={() => setViewMode('preview')}
                >
                  <Icon name="eye" />
                  Vista Previa
                </button>
              </div>
              <Button.Group>
                <Button onClick={onCancel}>Cancelar</Button>
                <Button primary loading={loading} onClick={handleSave}>
                  Guardar
                </Button>
              </Button.Group>
            </div>
          </div>

          <div className="content-container">
            {viewMode === 'edit' ? (
              <input
                name="title"
                value={inputs.title}
                onChange={handleInputs}
                placeholder="Título de la lección"
                className="editable-title"
              />
            ) : (
              <h2 className="title">{inputs.title}</h2>
            )}

            {renderContent()}

            {viewMode === 'preview' && (
              <div className="navigation">
                <button className="styled-button" disabled>
                  Vista Previa - No se puede navegar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </EditorStyles>
  );
};

export { LessonEditor };
